import React, { useCallback, useEffect, useRef } from "react";
import { RecordingWidgetState, useAssetLibraryProvider } from "@/state/providers/AssetLibraryProvider";
import { useTour } from "@providers/TourProvider";
import { MicStatusBar } from "./mic/MicStatusBar";
import { MicList } from "./mic/MicList";
import { trimMicLabel } from "./mic/micDevices";
import { DEFAULT_MIC_LABEL, type MicAction } from "./mic/micCopy";
import { useMic } from "./mic/MicProvider";
import { useSilenceMinute } from "./mic/useSilenceMinute";
import { useChromeExtension } from "@/state/providers/ChromeExtensionProvider";

const HELP_CENTER_URL = "https://help.gohappydoc.com/en/";

/**
 * The mic status bar as the widget's last row (EN-1499). Reads the mic provider and the recorder,
 * and owns the open/close lifecycle of the mic list that replaces the bar in place.
 */
const WidgetMicBar: React.FC = () => {
    const mic = useMic();
    const { openOptionsPage } = useChromeExtension();
    const { activeTour } = useTour();
    const {
        recorderControls: { recordingState, recordingMessage, audioStream, isExtensionTransport },
        recordingWidgetControls: { recordingWidgetState },
    } = useAssetLibraryProvider();

    const isRecordingMode = recordingState === "recording" || recordingState === "paused";
    // A start is in progress (gate, LOADING, or the recorder connecting): the mic is about to be
    // taken, so the bar offers no actions and no list.
    const isStarting =
        !isRecordingMode && (recordingWidgetState !== RecordingWidgetState.WAITING || recordingState === "loading");
    const showMic = !isExtensionTransport;
    // R23: only a live browser recording counts silence; paused time and the extension never do.
    const silent = useSilenceMinute(audioStream ?? null, recordingState === "recording" && showMic);
    const rootRef = useRef<HTMLDivElement>(null);
    const { listOpen, closeList } = mic;

    // The list never outlives the idle state: close it when a recording starts or is starting.
    useEffect(() => {
        if ((isRecordingMode || isStarting) && listOpen) closeList();
    }, [isRecordingMode, isStarting, listOpen, closeList]);

    // The probe holds the mic open, so the list must not outlive this bar (browser Back,
    // navigation, or anything else that unmounts the widget without a click).
    const listOpenRef = useRef(listOpen);
    listOpenRef.current = listOpen;
    const closeListRef = useRef(closeList);
    closeListRef.current = closeList;
    useEffect(
        () => () => {
            if (listOpenRef.current) closeListRef.current();
        },
        [],
    );

    // Click outside or Escape closes the list.
    useEffect(() => {
        if (!listOpen) return;
        const onPointerDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) closeList();
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") closeList();
        };
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [listOpen, closeList]);

    const onAction = useCallback(
        (a: MicAction) => {
            switch (a) {
                case "change":
                    void mic.openList();
                    break;
                case "allow":
                    void mic.allow();
                    break;
                case "check_again":
                case "try_again":
                    void mic.recheck();
                    break;
                case "get_help":
                    // Opens the same Help Center URL as the profile menu item. That item is also the Intercom launcher
                    // (id IntercomCustomLink, bound in Intercom.tsx); this button deliberately is not.
                    window.open(HELP_CENTER_URL, "_blank", "noopener,noreferrer");
                    break;
            }
        },
        [mic],
    );

    // On the extension transport, permission errors offer "Open Extension Settings".
    const lowered = recordingMessage?.message?.toLowerCase() ?? "";
    const isPermissionError = lowered.includes("permission") || lowered.includes("denied");
    const messageAction =
        isExtensionTransport && isPermissionError
            ? { label: "Open Extension Settings", onClick: () => openOptionsPage() }
            : undefined;

    const micLabel = isRecordingMode
        ? trimMicLabel(audioStream?.getAudioTracks()[0]?.label ?? "")
        : (mic.selected?.label ?? DEFAULT_MIC_LABEL);

    return (
        <div ref={rootRef} data-testid="mic-status-bar">
            <MicStatusBar
                mode={isRecordingMode ? "recording" : "idle"}
                micState={mic.state}
                platform={mic.platform}
                micLabel={micLabel}
                recordingStream={audioStream ?? null}
                showMic={showMic}
                message={recordingMessage ?? null}
                hideWarnings={!!activeTour}
                messageAction={messageAction}
                busy={mic.busy}
                starting={isStarting}
                paused={recordingState === "paused"}
                silent={silent}
                onAction={onAction}
                listOpen={listOpen}
                list={
                    <MicList
                        inputs={mic.inputs}
                        selectedId={mic.selected?.deviceId ?? null}
                        probeStream={mic.probeStream}
                        onPick={(d) => void mic.choose(d)}
                        onClose={closeList}
                    />
                }
            />
        </div>
    );
};

export default WidgetMicBar;
